import React, { useState, Component } from 'react';
import { Card } from './SubscriptionAppCard/Card';
import { CustomStylesheetSection } from './CustomStylesheetSection';
import { Info, ChevronDown, RefreshCw, Check, Type, AlignLeft, Monitor, Smartphone, ArrowLeft, ArrowRight } from 'lucide-react';
export function ThemingPanel({
  onBack,
  onNext
}: {
  onBack?: () => void;
  onNext?: () => void;
}) {
  // State for Colour Overrides
  const [primaryOverride, setPrimaryOverride] = useState(false);
  const [primaryColor, setPrimaryColor] = useState('#722F37');
  const [accentOverride, setAccentOverride] = useState(false);
  const [accentColor, setAccentColor] = useState('#D4A574');
  const [textOverride, setTextOverride] = useState(false);
  const [textColor, setTextColor] = useState('#1A1A2E');
  const [bgOverride, setBgOverride] = useState(false);
  const [bgColor, setBgColor] = useState('#FFFFFF');
  // State for Typography
  const [fontSource, setFontSource] = useState('inherit');
  const [headingWeight, setHeadingWeight] = useState('600');
  // State for Component Styling
  const [borderRadius, setBorderRadius] = useState('8px');
  const [buttonStyle, setButtonStyle] = useState('solid');
  const [cardShadow, setCardShadow] = useState('subtle');
  const [borderWeight, setBorderWeight] = useState('1px');
  // State for Preview
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  // State for Block Settings
  const [activeSettingsTab, setActiveSettingsTab] = useState<'product-browser' | 'frequency-picker' | 'order-summary' | 'pack-selector'>('product-browser');
  const [displayOptions, setDisplayOptions] = useState({
    showPrice: true,
    showDiscount: true,
    showStock: true,
    showTags: true,
    enableSearch: true,
    enableFilter: true
  });
  // Frequency Picker state
  const [freqCardStyle, setFreqCardStyle] = useState('horizontal');
  const [freqHighlightBadge, setFreqHighlightBadge] = useState('best-value');
  const [freqDefaultSelection, setFreqDefaultSelection] = useState('best-value-frequency');
  const [freqDisplayOptions, setFreqDisplayOptions] = useState({
    showSavings: true,
    showNextDelivery: true,
    showDiscountPct: true,
    showTierName: false
  });
  // Order Summary state
  const [summaryStyle, setSummaryStyle] = useState('inline');
  const [ctaButtonText, setCtaButtonText] = useState('Subscribe & Pay');
  const [summaryDisplayOptions, setSummaryDisplayOptions] = useState({
    showThumbnails: true,
    showLineItemPricing: true,
    showTotalSavings: true,
    showShippingEstimate: false,
    editableQuantities: true,
    showNextBillingDate: false
  });
  // Pack Selector state
  const [packSelectorStyle, setPackSelectorStyle] = useState('cards-with-images');
  const [packDefaultSelection, setPackDefaultSelection] = useState('middle');
  const [packDisplayOptions, setPackDisplayOptions] = useState({
    showPackImage: true,
    showPackDescription: true,
    showPriceRange: true,
    showTierBadge: true,
    showBottleCount: false
  });
  const colorPresets = ['#722F37', '#D4A574', '#E2E8F0', '#F1F5F9', '#0F172A', '#FFFFFF'];
  return <div className="flex flex-col gap-6 max-w-4xl mx-auto" data-id="element-5978" data-mp="392a9xj65brc9">
      {/* Section 1: Detected from Store */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-5979" data-mp="3dziv7smlcbhq">
        <div className="p-6" data-id="element-5980" data-mp="3irtt6pxrtn7n">
          <div className="flex items-center justify-between mb-4" data-id="element-5981" data-mp="3it5191y91h4k">
            <h2 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-5982" data-mp="3nrexlfvpmnrr">
              Detected from Store
            </h2>
            <div className="flex items-center gap-3" data-id="element-5983" data-mp="3rqnng9nmq1i1">
              <div className="flex items-center gap-1.5" data-id="element-5984" data-mp="m75dmabyis5">
                <div className="w-2 h-2 rounded-full bg-[#10B981]" data-id="element-5985" data-mp="mqjst0rup1f" />
                <span className="text-[12px] text-[#64748B]" data-id="element-5986" data-mp="5jz7180s1j9j">
                  Synced 2 min ago
                </span>
              </div>
              <button className="flex items-center gap-1 text-[12px] text-[#0EA5E9] hover:text-[#0284C7] font-medium" data-id="element-5987" data-mp="22fev7pnkkisa">
                <RefreshCw className="w-3 h-3" data-id="element-5988" data-mp="1o4i2mjee52u" />
                Refresh
              </button>
            </div>
          </div>

          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-5989" data-mp="3sv79nf35eddl">
            We read your active Shopify theme settings and product cards
            automatically. The widget inherits these by default.
          </p>

          <div className="flex items-start gap-4 mb-8" data-id="element-5990" data-mp="6o8ykckb2zt2">
            <div className="w-16 h-16 bg-white border border-[#E2E8F0] rounded-lg flex items-center justify-center p-2" data-id="element-5991" data-mp="3f30wm1vakmid">
              <span className="text-2xl" data-id="element-5992" data-mp="3a4g2mk6muz2d">🍇</span>
            </div>
            <div data-id="element-5993" data-mp="3a23e1o6ddqgc">
              <h3 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-5994" data-mp="3nv2upthw1qph">
                Oak Valley Estate Wines
              </h3>
              <p className="text-[13px] text-[#64748B]" data-id="element-5995" data-mp="3j3jmhqdmjihx">
                Theme: Dawn 15.0 (customised) · Published
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mb-4" data-id="element-5996" data-mp="2rdolgwgem4df">
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full" data-id="element-5997" data-mp="2rd6jdj728vxl">
              <div className="w-4 h-4 rounded-full bg-[#722F37]" data-id="element-5998" data-mp="2h7f3n139ar7y" />
              <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-5999" data-mp="2m1018s0xtj6m">
                Primary
              </span>
              <span className="text-[13px] text-[#64748B]" data-id="element-6000" data-mp="27d56z77a76rf">#722F37</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full" data-id="element-6001" data-mp="2cawxbwsa2kti">
              <div className="w-4 h-4 rounded-full bg-[#D4A574]" data-id="element-6002" data-mp="2zxwqwq5bkulx" />
              <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-6003" data-mp="34y15gamo3f9i">
                Accent
              </span>
              <span className="text-[13px] text-[#64748B]" data-id="element-6004" data-mp="2q1a4hwxchvhs">#D4A574</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full" data-id="element-6005" data-mp="2uzleq8rjy80j">
              <div className="w-4 h-4 rounded-full bg-[#1A1A2E]" data-id="element-6006" data-mp="1elbqrfr82la8" />
              <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-6007" data-mp="1jmsr9a5so5qa">
                Text
              </span>
              <span className="text-[13px] text-[#64748B]" data-id="element-6008" data-mp="3m73jsm0t8utu">#1A1A2E</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full" data-id="element-6009" data-mp="3had3pyp09z8f">
              <div className="w-4 h-4 rounded-full bg-[#FFFDF8] border border-[#E2E8F0]" data-id="element-6010" data-mp="3ch0q0jlv7cug" />
              <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-6011" data-mp="37ipiifj1lefe">
                Background
              </span>
              <span className="text-[13px] text-[#64748B]" data-id="element-6012" data-mp="32lqpk0b47dis">#FFFDF8</span>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 mb-6" data-id="element-6013" data-mp="2ykx0h3lxrsob">
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full" data-id="element-6014" data-mp="2tixzg0do57uj">
              <Type className="w-4 h-4 text-[#64748B]" data-id="element-6015" data-mp="2oly008bbr3ce" />
              <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-6016" data-mp="rs4cut59jqd1">
                Headings
              </span>
              <span className="text-[13px] text-[#64748B]" data-id="element-6017" data-mp="mt02w0ailoln">
                Playfair Display
              </span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full" data-id="element-6018" data-mp="uc9bvsf3zla6">
              <AlignLeft className="w-4 h-4 text-[#64748B]" data-id="element-6019" data-mp="z9sqitfpy02n" />
              <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-6020" data-mp="143n6pmw47wwz">
                Body
              </span>
              <span className="text-[13px] text-[#64748B]" data-id="element-6021" data-mp="190dp4gve74lk">
                Assistant (sans-serif)
              </span>
            </div>
          </div>

          <div className="bg-[#F0F9FF] border border-[#E0F2FE] rounded-lg p-4 flex gap-3" data-id="element-6022" data-mp="gcrit0us5lv0">
            <Info className="w-5 h-5 text-[#0EA5E9] flex-shrink-0 mt-0.5" data-id="element-6023" data-mp="lc477q1av0zp" />
            <p className="text-[13px] text-[#475569] leading-relaxed" data-id="element-6024" data-mp="le74mv4s7weg">
              Pulled from your theme's{' '}
              <code className="bg-[#E0F2FE] px-1 py-0.5 rounded text-[#0284C7] font-mono text-xs" data-id="element-6025" data-mp="q8unoeh0nyln">
                settings_data.json
              </code>{' '}
              and checkout branding API. Product cards in the widget use your
              theme's native product card markup where possible. Everything
              below is optional — only override what you want to change.
            </p>
          </div>
        </div>
      </Card>

      {/* Section 2: Colour Overrides */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-6026" data-mp="2wmaxnc7k89q2">
        <div className="p-6" data-id="element-6027" data-mp="2rspul5r40pk0">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-6028" data-mp="2rrer771ktjtf">
            Colour Overrides
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-6029" data-mp="31jb3bm0hzagn">
            Override specific colours from your theme. Anything left on
            "Inherit" uses the detected values above.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8" data-id="element-6030" data-mp="3ajzi4ixpteul">
            {/* Primary Colour */}
            <div data-id="element-6031" data-mp="1l4syyr4u60ae">
              <div className="flex items-center justify-between mb-3" data-id="element-6032" data-mp="1l49izfyqxlz0">
                <label className="text-[14px] font-semibold text-[#0F172A]" data-id="element-6033" data-mp="1g5yab8k6thid">
                  Primary Colour
                </label>
                <div className="flex items-center bg-[#F1F5F9] rounded-lg p-0.5 border border-[#E2E8F0]" data-id="element-6034" data-mp="3rucsu92z4zw0">
                  <button onClick={() => setPrimaryOverride(false)} className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${!primaryOverride ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-6035" data-mp="lp7ht4qhcog">
                    Inherit
                  </button>
                  <button onClick={() => setPrimaryOverride(true)} className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${primaryOverride ? 'bg-white text-[#0EA5E9] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-6036" data-mp="391sdzvk40vb7">
                    Override
                  </button>
                </div>
              </div>

              <div className={`transition-opacity ${!primaryOverride ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-6037" data-mp="345tmw07ecz73">
                <div className="flex gap-3 mb-3" data-id="element-6038" data-mp="xbga6fn6i65c">
                  <label className="w-[42px] h-[42px] rounded-lg border border-[#E2E8F0] shadow-sm flex-shrink-0 overflow-hidden cursor-pointer" data-id="element-6039" data-mp="12b2rb064o6jf">
                    <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="w-[58px] h-[58px] -m-2 cursor-pointer border-none p-0" style={{
                    backgroundColor: primaryColor
                  }} data-id="element-6040" data-mp="3sp2x494rer50" />
                  </label>
                  <div className="flex-1 relative" data-id="element-6041" data-mp="3npq9hm62dg1d">
                    <input type="text" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} className="w-full h-[42px] px-3 border border-[#CBD5E1] rounded-lg text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-6042" data-mp="fbrf2oyqq2pz" />
                  </div>
                </div>
                <div className="flex gap-2 mb-3" data-id="element-6043" data-mp="abd716x52sry">
                  {colorPresets.slice(0, 4).map(color => <button key={color} onClick={() => setPrimaryColor(color)} className="w-8 h-6 rounded border border-[#E2E8F0]" style={{
                  backgroundColor: color
                }} data-id="element-6044" data-mp="2q2iuh40hcakw" />)}
                </div>
                <p className="text-[13px] text-[#0F172A]" data-id="element-6045" data-mp="1d7my1dbtm563">
                  Buttons, active states, progress bar, badges
                </p>
              </div>
            </div>

            {/* Accent Colour */}
            <div data-id="element-6046" data-mp="1m3vvwnk5gvq4">
              <div className="flex items-center justify-between mb-3" data-id="element-6047" data-mp="1i4wo8v71akuz">
                <label className="text-[14px] font-semibold text-[#0F172A]" data-id="element-6048" data-mp="1i2sj69xefft4">
                  Accent Colour
                </label>
                <div className="flex items-center bg-[#F1F5F9] rounded-lg p-0.5 border border-[#E2E8F0]" data-id="element-6049" data-mp="pghjd1hm9ixd">
                  <button onClick={() => setAccentOverride(false)} className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${!accentOverride ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-6050" data-mp="137mbzjmkhay7">
                    Inherit
                  </button>
                  <button onClick={() => setAccentOverride(true)} className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${accentOverride ? 'bg-white text-[#0EA5E9] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-6051" data-mp="6pzrez4lyrd2">
                    Override
                  </button>
                </div>
              </div>

              <div className={`transition-opacity ${!accentOverride ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-6052" data-mp="2fkjhult17vlp">
                <div className="flex gap-3 mb-3" data-id="element-6053" data-mp="2kicjd1je41fn">
                  <label className="w-[42px] h-[42px] rounded-lg border border-[#E2E8F0] shadow-sm flex-shrink-0 overflow-hidden cursor-pointer" data-id="element-6054" data-mp="1mvvtghczkq3x">
                    <input type="color" value={accentColor} onChange={e => setAccentColor(e.target.value)} className="w-[58px] h-[58px] -m-2 cursor-pointer border-none p-0" style={{
                    backgroundColor: accentColor
                  }} data-id="element-6055" data-mp="1rup4m34sw50x" />
                  </label>
                  <div className="flex-1 relative" data-id="element-6056" data-mp="387x5tuer5gto">
                    <input type="text" value={accentColor} onChange={e => setAccentColor(e.target.value)} className="w-full h-[42px] px-3 border border-[#CBD5E1] rounded-lg text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-6057" data-mp="3d7jj23ukyx2h" />
                  </div>
                </div>
                <div className="flex gap-2 mb-3" data-id="element-6058" data-mp="rtldrmkyn0op">
                  {colorPresets.slice(1, 5).map(color => <button key={color} onClick={() => setAccentColor(color)} className="w-8 h-6 rounded border border-[#E2E8F0]" style={{
                  backgroundColor: color
                }} data-id="element-6059" data-mp="mwbrugws575m" />)}
                </div>
                <p className="text-[13px] text-[#0F172A]" data-id="element-6060" data-mp="du2k1yswbrvx">
                  Discount badges, highlights, secondary actions
                </p>
              </div>
            </div>

            {/* Text Colour */}
            <div data-id="element-6061" data-mp="3yinrbaf6r6n">
              <div className="flex items-center justify-between mb-3" data-id="element-6062" data-mp="3z0syxf28fqp">
                <label className="text-[14px] font-semibold text-[#0F172A]" data-id="element-6063" data-mp="239a0qdy7rzpi">
                  Text Colour
                </label>
                <div className="flex items-center bg-[#F1F5F9] rounded-lg p-0.5 border border-[#E2E8F0]" data-id="element-6064" data-mp="1y8w3zs22cc6s">
                  <button onClick={() => setTextOverride(false)} className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${!textOverride ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-6065" data-mp="zyw5tr2eke5o">
                    Inherit
                  </button>
                  <button onClick={() => setTextOverride(true)} className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${textOverride ? 'bg-white text-[#0EA5E9] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-6066" data-mp="r012cmqqti62">
                    Override
                  </button>
                </div>
              </div>

              <div className={`transition-opacity ${!textOverride ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-6067" data-mp="876wqjf0f2ei">
                <div className="flex gap-3 mb-3" data-id="element-6068" data-mp="3pfk3babkha4s">
                  <label className="w-[42px] h-[42px] rounded-lg border border-[#E2E8F0] shadow-sm flex-shrink-0 overflow-hidden cursor-pointer" data-id="element-6069" data-mp="3phwrw6btyiqt">
                    <input type="color" value={textColor} onChange={e => setTextColor(e.target.value)} className="w-[58px] h-[58px] -m-2 cursor-pointer border-none p-0" style={{
                    backgroundColor: textColor
                  }} data-id="element-6070" data-mp="3asd2vyjpjuk5" />
                  </label>
                  <div className="flex-1 relative" data-id="element-6071" data-mp="3fshi7l8vqj8s">
                    <input type="text" value={textColor} onChange={e => setTextColor(e.target.value)} className="w-full h-[42px] px-3 border border-[#CBD5E1] rounded-lg text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-6072" data-mp="3fqmy295fmv5r" />
                  </div>
                </div>
                <p className="text-[13px] text-[#0F172A]" data-id="element-6073" data-mp="7e8xezclmwr6">
                  Headings and body text within the widget
                </p>
              </div>
            </div>

            {/* Background Colour */}
            <div data-id="element-6074" data-mp="cbhj39wyhix6">
              <div className="flex items-center justify-between mb-3" data-id="element-6075" data-mp="cc0xignedp6g">
                <label className="text-[14px] font-semibold text-[#0F172A]" data-id="element-6076" data-mp="c9oavq6p2qn2">
                  Background Colour
                </label>
                <div className="flex items-center bg-[#F1F5F9] rounded-lg p-0.5 border border-[#E2E8F0]" data-id="element-6077" data-mp="od0q09izdjkd">
                  <button onClick={() => setBgOverride(false)} className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${!bgOverride ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-6078" data-mp="tbu15vasoyhd">
                    Inherit
                  </button>
                  <button onClick={() => setBgOverride(true)} className={`px-3 py-1 text-[12px] font-medium rounded-md transition-all ${bgOverride ? 'bg-white text-[#0EA5E9] shadow-sm' : 'text-[#64748B] hover:text-[#0F172A]'}`} data-id="element-6079" data-mp="173ie20svbqze">
                    Override
                  </button>
                </div>
              </div>

              <div className={`transition-opacity ${!bgOverride ? 'opacity-50 pointer-events-none' : ''}`} data-id="element-6080" data-mp="3dw17ebhgqzz6">
                <div className="flex gap-3 mb-3" data-id="element-6081" data-mp="3iuch8m7rdahe">
                  <label className="w-[42px] h-[42px] rounded-lg border border-[#E2E8F0] shadow-sm flex-shrink-0 overflow-hidden cursor-pointer" data-id="element-6082" data-mp="3batj18py1ao8">
                    <input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)} className="w-[58px] h-[58px] -m-2 cursor-pointer border-none p-0" style={{
                    backgroundColor: bgColor
                  }} data-id="element-6083" data-mp="36i0bye705q8z" />
                  </label>
                  <div className="flex-1 relative" data-id="element-6084" data-mp="3u9fmgu8xndvb">
                    <input type="text" value={bgColor} onChange={e => setBgColor(e.target.value)} className="w-full h-[42px] px-3 border border-[#CBD5E1] rounded-lg text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-6085" data-mp="3u6tewum1kyd3" />
                  </div>
                </div>
                <p className="text-[13px] text-[#0F172A]" data-id="element-6086" data-mp="28vkyrollzer8">
                  Widget card backgrounds. Set to transparent to inherit page
                  background.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Section 3: Typography Overrides */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-6087" data-mp="2ykhy6znzf4fg">
        <div className="p-6" data-id="element-6088" data-mp="2yhlz380ee9pf">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-6089" data-mp="2kkp9tazxh0an">
            Typography Overrides
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-6090" data-mp="2omtzjyn99cs0">
            By default the widget inherits your theme's fonts. Override here if
            the widget needs a different typeface.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6091" data-mp="3h950jaerxfpc">
            <div data-id="element-6092" data-mp="33jbf722h9dkd">
              <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-6093" data-mp="33jus24d9tbrj">
                Font Source
              </label>
              <div className="relative" data-id="element-6094" data-mp="37ityjtenhgl3">
                <select value={fontSource} onChange={e => setFontSource(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-6095" data-mp="3vahdre2jv37j">
                  <option value="inherit" data-id="element-6096" data-mp="1jnnpe22611r8">Inherit from theme</option>
                  <option value="google" data-id="element-6097" data-mp="1ybsafcooxpd6">Google Fonts</option>
                  <option value="custom" data-id="element-6098" data-mp="1yb8t9y6v7507">Custom Font</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-6099" data-mp="1te07lnmiciu7" />
              </div>
              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-6100" data-mp="15oxpkenyonz6">
                Inherit uses whatever the merchant's Shopify theme specifies
              </p>
            </div>

            <div data-id="element-6101" data-mp="1fmlozhwiijno">
              <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-6102" data-mp="1fixvgir2hx8y">
                Heading Weight
              </label>
              <div className="relative" data-id="element-6103" data-mp="2qzo408iyr15q">
                <select value={headingWeight} onChange={e => setHeadingWeight(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-6104" data-mp="2qymjzejyz4bv">
                  <option value="400" data-id="element-6105" data-mp="3fsu86osfeop5">Regular (400)</option>
                  <option value="500" data-id="element-6106" data-mp="3tr26p1541y2f">Medium (500)</option>
                  <option value="600" data-id="element-6107" data-mp="3orfsophgkdsk">Semi-bold (600)</option>
                  <option value="700" data-id="element-6108" data-mp="3otabny965vu0">Bold (700)</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-6109" data-mp="7hhp82b46b19" />
              </div>
              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-6110" data-mp="gb4h3hc7au56">
                Weight applied to step titles and product names
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Section 4: Component Styling */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-6111" data-mp="qa21c82lg1id">
        <div className="p-6" data-id="element-6112" data-mp="laz4vc0oj3m0">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-6113" data-mp="lbs8i44cdczx">
            Component Styling
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-6114" data-mp="14x0npomlg5q8">
            Shape and feel of interactive elements in the widget
          </p>

          {/* Border Radius */}
          <div className="mb-8" data-id="element-6115" data-mp="ux1k865175k8">
            <label className="block text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-4" data-id="element-6116" data-mp="v05u9k56gvit">
              Border Radius
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6117" data-mp="m4q4pfbgh2j1">
              {[{
              id: '0px',
              label: 'Sharp (0px)',
              class: 'rounded-none'
            }, {
              id: '4px',
              label: 'Subtle (4px)',
              class: 'rounded-[4px]'
            }, {
              id: '8px',
              label: 'Rounded (8px)',
              class: 'rounded-[8px]'
            }, {
              id: '16px',
              label: 'Pill (16px)',
              class: 'rounded-[16px]'
            }].map(option => <div key={option.id} onClick={() => setBorderRadius(option.id)} className={`
                    flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-all
                    ${borderRadius === option.id ? 'border-[#0EA5E9] bg-[#F0F9FF] ring-1 ring-[#0EA5E9]' : 'border-[#E2E8F0] hover:border-[#CBD5E1] bg-white'}
                  `} data-id="element-6118" data-mp="7abht3ort8z3">
                  <div className={`w-12 h-8 border border-[#94A3B8] ${option.class}`} data-id="element-6119" data-mp="1v7ufjr1iqwez" />

                  <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-6120" data-mp="2ssp3n6jdah69">
                    {option.label}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Button Style */}
          <div className="mb-8" data-id="element-6121" data-mp="2iu0yh8chdwjz">
            <label className="block text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-4" data-id="element-6122" data-mp="2euioh7m775vx">
              Button Style
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6123" data-mp="17i8gr4p26j7i">
              {[{
              id: 'solid',
              label: 'Solid fill',
              preview: <div className="px-3 py-1.5 bg-[#722F37] text-white text-xs font-medium" style={{
                borderRadius: borderRadius
              }} data-id="element-6124" data-mp="3f2yt9qyv3e20">
                      Add to box
                    </div>
            }, {
              id: 'outline',
              label: 'Outline',
              preview: <div className="px-3 py-1.5 border border-[#722F37] text-[#722F37] text-xs font-medium" style={{
                borderRadius: borderRadius
              }} data-id="element-6125" data-mp="3su3ngdjgne4y">
                      Add to box
                    </div>
            }, {
              id: 'rounded-fill',
              label: 'Rounded fill',
              preview: <div className="px-3 py-1.5 bg-[#722F37] text-white text-xs font-medium rounded-full" data-id="element-6126" data-mp="apig5tvt1oek">
                      Add to box
                    </div>
            }, {
              id: 'rounded-outline',
              label: 'Rounded outline',
              preview: <div className="px-3 py-1.5 border border-[#722F37] text-[#722F37] text-xs font-medium rounded-full" data-id="element-6127" data-mp="2he57dppv7ru8">
                      Add to box
                    </div>
            }].map(option => <div key={option.id} onClick={() => setButtonStyle(option.id)} className={`
                    flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-all
                    ${buttonStyle === option.id ? 'border-[#0EA5E9] bg-[#F0F9FF] ring-1 ring-[#0EA5E9]' : 'border-[#E2E8F0] hover:border-[#CBD5E1] bg-white'}
                  `} data-id="element-6128" data-mp="1h5nos0jqqxm9">
                  {option.preview}
                  <span className="text-[14px] font-medium text-[#0F172A]" data-id="element-6129" data-mp="yf5wty6qg61y">
                    {option.label}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Dropdowns */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6130" data-mp="18ctyz96o4fu5">
            <div data-id="element-6131" data-mp="knj2i3wcp7lt">
              <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-6132" data-mp="kmzhuffbg543">
                Card Shadow
              </label>
              <div className="relative" data-id="element-6133" data-mp="fnwpnvkfptdj">
                <select value={cardShadow} onChange={e => setCardShadow(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-6134" data-mp="2rbrtrpcs55hw">
                  <option value="none" data-id="element-6135" data-mp="2mcf4kxyfrmc5">None</option>
                  <option value="subtle" data-id="element-6136" data-mp="2mcyepshuh6fm">Subtle</option>
                  <option value="medium" data-id="element-6137" data-mp="3a1rb1q2dpgar">Medium</option>
                  <option value="strong" data-id="element-6138" data-mp="3a06gi0g2dxcw">Strong</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-6139" data-mp="3f0u9usrrmqaa" />
              </div>
              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-6140" data-mp="352wms8ln6zj9">
                Shadow intensity on product cards and frequency options
              </p>
            </div>

            <div data-id="element-6141" data-mp="1poikhj3si8w6">
              <label className="block text-[14px] font-semibold text-[#0F172A] mb-3" data-id="element-6142" data-mp="1to150drp8hyh">
                Border Weight
              </label>
              <div className="relative" data-id="element-6143" data-mp="490oodxw018">
                <select value={borderWeight} onChange={e => setBorderWeight(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-6144" data-mp="74ymcpseopo">
                  <option value="0px" data-id="element-6145" data-mp="3dk700zwniibo">None (0px)</option>
                  <option value="1px" data-id="element-6146" data-mp="3dlrsmjzenr6w">Thin (1px)</option>
                  <option value="2px" data-id="element-6147" data-mp="38kknksond63x">Medium (2px)</option>
                  <option value="3px" data-id="element-6148" data-mp="38n6sslo2f9iz">Thick (3px)</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-6149" data-mp="33mj1rzzu6sor" />
              </div>
              <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-6150" data-mp="11ue2g3g0ct40">
                Border thickness on cards and interactive elements
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Section 5: Custom Stylesheet */}
      <CustomStylesheetSection data-id="element-6151" data-mp="15c24equr15q9" />

      {/* Section 6: Block Settings */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-6152" data-mp="19d3uoi6ou7g3">
        <div className="p-6" data-id="element-6153" data-mp="1e4vl8spugd68">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-6154" data-mp="1e4lz5g7x6q5t">
            Block Settings
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-6155" data-mp="1j2nfvuhvide4">
            Per-block configuration for the storefront widget. Select a block to
            see its settings.
          </p>

          {/* Sub-tabs */}
          <div className="flex border-b border-[#E2E8F0] mb-6" data-id="element-6156" data-mp="qft6bsmlgyff">
            {([{
            id: 'product-browser',
            label: 'Product Browser'
          }, {
            id: 'frequency-picker',
            label: 'Frequency Picker'
          }, {
            id: 'order-summary',
            label: 'Order Summary'
          }, {
            id: 'pack-selector',
            label: 'Pack Selector'
          }] as const).map(tab => <button key={tab.id} onClick={() => setActiveSettingsTab(tab.id)} className={`text-[13px] font-semibold px-[18px] py-3 border-b-2 transition-colors duration-200 ${activeSettingsTab === tab.id ? 'border-[#0EA5E9] text-[#0EA5E9]' : 'border-transparent text-[#94A3B8] hover:text-[#64748B]'}`} data-id="element-6157" data-mp="2bugrt9qwgcpi">
                {tab.label}
              </button>)}
          </div>

          {activeSettingsTab === 'product-browser' && <div data-id="element-6158" data-mp="3gkfdo3srsbud">
              <label className="block text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-4" data-id="element-6159" data-mp="3gjw3xad9wtrf">
                Display Options
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6160" data-mp="3pldhom37kjcy">
                {[{
              key: 'showPrice',
              label: 'Show price per bottle',
              desc: 'Individual pricing beneath each product'
            }, {
              key: 'showDiscount',
              label: 'Show discount badge',
              desc: 'Badge showing "10% off" per the selected frequency'
            }, {
              key: 'showStock',
              label: 'Show stock indicator',
              desc: 'Low stock or sold out labels'
            }, {
              key: 'showTags',
              label: 'Show product tags',
              desc: 'Varietal, region, or vintage tags on cards'
            }, {
              key: 'enableSearch',
              label: 'Enable search',
              desc: 'Search bar at top of product grid'
            }, {
              key: 'enableFilter',
              label: 'Enable filter tabs',
              desc: 'Tabs to filter by type (Red, White, Rosé, etc.)'
            }].map(({
              key,
              label,
              desc
            }) => <div key={key} onClick={() => setDisplayOptions(prev => ({
              ...prev,
              [key]: !prev[key as keyof typeof prev]
            }))} className={`rounded-lg p-4 flex gap-3 border cursor-pointer transition-colors ${displayOptions[key as keyof typeof displayOptions] ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`} data-id="element-6161" data-mp="3a6rxs00t314n">
                    <div className="pt-0.5 flex-shrink-0" data-id="element-6162" data-mp="2f1ykn6ynpbsp">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${displayOptions[key as keyof typeof displayOptions] ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-6163" data-mp="2k3nyeud9k3kh">
                        {displayOptions[key as keyof typeof displayOptions] && <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-6164" data-mp="2xv2ht2b326s2" />}
                      </div>
                    </div>
                    <div data-id="element-6165" data-mp="32rswpmb5iwbw">
                      <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-6166" data-mp="32v71t2b79jn1">
                        {label}
                      </div>
                      <div className="text-[13px] text-[#64748B]" data-id="element-6167" data-mp="3bubys47454x3">{desc}</div>
                    </div>
                  </div>)}
              </div>
            </div>}

          {activeSettingsTab === 'frequency-picker' && <div className="space-y-6" data-id="element-6168" data-mp="1hh2i9sg5gj5c">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6169" data-mp="1hi3y0a841tte">
                <div data-id="element-6170" data-mp="19xa13bbpjcfz">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-6171" data-mp="19yl61egw2q8o">
                    Card Style
                  </label>
                  <div className="relative" data-id="element-6172" data-mp="105xbyrhan0dn">
                    <select value={freqCardStyle} onChange={e => setFreqCardStyle(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-6173" data-mp="106yqj5xiq504">
                      <option value="horizontal" data-id="element-6174" data-mp="1juo7ei3x2kuk">Horizontal cards</option>
                      <option value="vertical" data-id="element-6175" data-mp="1jsv1j514o4nm">Vertical cards</option>
                      <option value="compact" data-id="element-6176" data-mp="1eujs2veqvw5x">Compact radio list</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-6177" data-mp="8gki3p89xkvu" />
                  </div>
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-6178" data-mp="4xllpslz9cv5">
                    How frequency options are displayed in the widget
                  </p>
                </div>
                <div data-id="element-6179" data-mp="9ulqm073c9ko">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-6180" data-mp="3s2l09s3eu8ii">
                    Highlight Badge
                  </label>
                  <div className="relative" data-id="element-6181" data-mp="3w2bnxmxn48it">
                    <select value={freqHighlightBadge} onChange={e => setFreqHighlightBadge(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-6182" data-mp="3icrp2s76jves">
                      <option value="none" data-id="element-6183" data-mp="3dege2e55feuz">None</option>
                      <option value="best-value" data-id="element-6184" data-mp="2zdnirr70wfqt">Best value</option>
                      <option value="most-popular" data-id="element-6185" data-mp="2zeff548g2hi5">Most popular</option>
                      <option value="recommended" data-id="element-6186" data-mp="34bozi5gz82z4">Recommended</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-6187" data-mp="2krzxg2p3356d" />
                  </div>
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-6188" data-mp="2fu6ox30b5z2x">
                    Automatically badge the frequency with the highest discount
                  </p>
                </div>
              </div>
              <div data-id="element-6189" data-mp="33pswuv0i0wz0">
                <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-6190" data-mp="33p13zryaddcf">
                  Default Selection
                </label>
                <div className="relative" data-id="element-6191" data-mp="2yols74tnkdwk">
                  <select value={freqDefaultSelection} onChange={e => setFreqDefaultSelection(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-6192" data-mp="3dg42clztze80">
                    <option value="first-frequency" data-id="element-6193" data-mp="3hisep3nr739q">First frequency</option>
                    <option value="best-value-frequency" data-id="element-6194" data-mp="1fstjrw4scc0u">
                      Best value frequency
                    </option>
                    <option value="most-frequent" data-id="element-6195" data-mp="1kpjwqalamri1">Most frequent option</option>
                    <option value="none" data-id="element-6196" data-mp="1piwec0rk1y1a">None — force a choice</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-6197" data-mp="1tidimqv8wp4v" />
                </div>
                <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-6198" data-mp="wvijc87f3cjt">
                  Which frequency is pre-selected when the step loads
                </p>
              </div>
              <div data-id="element-6199" data-mp="15sj7qeuhlan9">
                <label className="block text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-4" data-id="element-6200" data-mp="15w70hbs3xt0x">
                  Display Options
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6201" data-mp="2h8ho73rzuyyi">
                  {[{
                key: 'showSavings',
                label: 'Show savings amount',
                desc: 'Display "Save $XX per shipment" alongside the percentage'
              }, {
                key: 'showNextDelivery',
                label: 'Show next delivery date',
                desc: 'Estimated first delivery below each option'
              }, {
                key: 'showDiscountPct',
                label: 'Show discount percentage',
                desc: 'Display the discount percentage on each frequency card'
              }, {
                key: 'showTierName',
                label: 'Show tier name',
                desc: 'Display the loyalty tier earned at each frequency'
              }].map(({
                key,
                label,
                desc
              }) => <div key={key} onClick={() => setFreqDisplayOptions(prev => ({
                ...prev,
                [key]: !prev[key as keyof typeof prev]
              }))} className={`rounded-lg p-4 flex gap-3 border cursor-pointer transition-colors ${freqDisplayOptions[key as keyof typeof freqDisplayOptions] ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`} data-id="element-6202" data-mp="1zn38vjuxpw5q">
                      <div className="pt-0.5 flex-shrink-0" data-id="element-6203" data-mp="1w5fbzav1w9qc">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${freqDisplayOptions[key as keyof typeof freqDisplayOptions] ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-6204" data-mp="1id8tvj8c7so9">
                          {freqDisplayOptions[key as keyof typeof freqDisplayOptions] && <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-6205" data-mp="2f2ykmddljqc8" />}
                        </div>
                      </div>
                      <div data-id="element-6206" data-mp="212nu5bg78drl">
                        <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-6207" data-mp="2620ftsvc3esl">
                          {label}
                        </div>
                        <div className="text-[13px] text-[#64748B]" data-id="element-6208" data-mp="pmz60gr7l8p7">{desc}</div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>}

          {activeSettingsTab === 'order-summary' && <div className="space-y-6" data-id="element-6209" data-mp="m7xogtkgj62l">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6210" data-mp="i4snivg0zcg2">
                <div data-id="element-6211" data-mp="i6ls8572vmlf">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-6212" data-mp="w4k7dct83n1z">
                    Summary Style
                  </label>
                  <div className="relative" data-id="element-6213" data-mp="r5qrxeudllz6">
                    <select value={summaryStyle} onChange={e => setSummaryStyle(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-6214" data-mp="3f3fcg9im86a">
                      <option value="inline" data-id="element-6215" data-mp="8e6fbman1o7e">Inline list</option>
                      <option value="sidebar" data-id="element-6216" data-mp="3gu9j3gymqsqg">Sidebar panel</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-6217" data-mp="3bv6f7zudysk2" />
                  </div>
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-6218" data-mp="2qd545c8zyjad">
                    How the order summary is displayed on the review step
                  </p>
                </div>
                <div data-id="element-6219" data-mp="308zsmnzwj8l2">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-6220" data-mp="308ge7h9gn2bs">
                    CTA Button Text
                  </label>
                  <input type="text" value={ctaButtonText} onChange={e => setCtaButtonText(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20" data-id="element-6221" data-mp="39b94bh6wp801" />
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-6222" data-mp="3nykh1metqy1q">
                    Text on the final checkout button
                  </p>
                </div>
              </div>
              <div data-id="element-6223" data-mp="u4l75uv8utg">
                <label className="block text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-4" data-id="element-6224" data-mp="vpfcud9qbqs">
                  Display Options
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6225" data-mp="1hatqpaw062jw">
                  {[{
                key: 'showThumbnails',
                label: 'Show wine thumbnails',
                desc: 'Small product images in the summary list'
              }, {
                key: 'showLineItemPricing',
                label: 'Show line item pricing',
                desc: 'Price per wine and per-bottle in the review'
              }, {
                key: 'showTotalSavings',
                label: 'Show total savings',
                desc: '"You\'re saving $XX with your subscription" line'
              }, {
                key: 'showShippingEstimate',
                label: 'Show shipping estimate',
                desc: 'Estimated shipping cost or "Free shipping" on the summary'
              }, {
                key: 'editableQuantities',
                label: 'Editable quantities',
                desc: 'Allow changing wine quantities from the summary before checkout'
              }, {
                key: 'showNextBillingDate',
                label: 'Show next billing date',
                desc: 'When the first payment will be charged'
              }].map(({
                key,
                label,
                desc
              }) => <div key={key} onClick={() => setSummaryDisplayOptions(prev => ({
                ...prev,
                [key]: !prev[key as keyof typeof prev]
              }))} className={`rounded-lg p-4 flex gap-3 border cursor-pointer transition-colors ${summaryDisplayOptions[key as keyof typeof summaryDisplayOptions] ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`} data-id="element-6226" data-mp="4z41ct73we5p">
                      <div className="pt-0.5 flex-shrink-0" data-id="element-6227" data-mp="1ley9qj01e3g5">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${summaryDisplayOptions[key as keyof typeof summaryDisplayOptions] ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-6228" data-mp="1leezlogmojco">
                          {summaryDisplayOptions[key as keyof typeof summaryDisplayOptions] && <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-6229" data-mp="1swmqis5sgt9z" />}
                        </div>
                      </div>
                      <div data-id="element-6230" data-mp="22p28hph35huv">
                        <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-6231" data-mp="22n7m06q61hoo">
                          {label}
                        </div>
                        <div className="text-[13px] text-[#64748B]" data-id="element-6232" data-mp="19zvacrljmua5">{desc}</div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>}

          {activeSettingsTab === 'pack-selector' && <div className="space-y-6" data-id="element-6233" data-mp="1jyax6idjfhdu">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-6234" data-mp="1jvy8lmd9y8rt">
                <div data-id="element-6235" data-mp="2qdblc1wbmv9v">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-6236" data-mp="2qdttnww9czy5">
                    Selector Style
                  </label>
                  <div className="relative" data-id="element-6237" data-mp="vaf6dmaev64l">
                    <select value={packSelectorStyle} onChange={e => setPackSelectorStyle(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-6238" data-mp="v93vl2ijsy3z">
                      <option value="cards-with-images" data-id="element-6239" data-mp="ckgsgpjxmit1">
                        Cards with images
                      </option>
                      <option value="cards-without-images" data-id="element-6240" data-mp="7m5i8dpq66aa">
                        Cards without images
                      </option>
                      <option value="compact-button-group" data-id="element-6241" data-mp="le26g6o9vw1e">
                        Compact button group
                      </option>
                      <option value="radio-list" data-id="element-6242" data-mp="3p1m0jfgp6pkx">Radio list</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-6243" data-mp="3l08w99t96ug4" />
                  </div>
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-6244" data-mp="3aur468n0ukt0">
                    How pack size options are presented on Step 1
                  </p>
                </div>
                <div data-id="element-6245" data-mp="30z79fpk71s5u">
                  <label className="block text-[12px] font-medium text-[#475569] mb-1.5" data-id="element-6246" data-mp="30zpenbou3gpw">
                    Default Selection
                  </label>
                  <div className="relative" data-id="element-6247" data-mp="3tma4u8tkpmrv">
                    <select value={packDefaultSelection} onChange={e => setPackDefaultSelection(e.target.value)} className="w-full h-[40px] px-3 bg-white border border-[#CBD5E1] rounded-[6px] text-[14px] text-[#0F172A] focus:outline-none focus:border-[#0EA5E9] focus:ring-[3px] focus:ring-[#0EA5E9]/20 appearance-none cursor-pointer" data-id="element-6248" data-mp="3tncxal6jnx9w">
                      <option value="first" data-id="element-6249" data-mp="289gu5d6tsk85">First pack size</option>
                      <option value="middle" data-id="element-6250" data-mp="2885qddddrch1">Middle pack size</option>
                      <option value="largest" data-id="element-6251" data-mp="2d9mmkvkx6ara">Largest pack size</option>
                      <option value="none" data-id="element-6252" data-mp="2tfg6gfd4j9iw">None — force a choice</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-6253" data-mp="2oog773gj8fd8" />
                  </div>
                  <p className="mt-1.5 text-[12px] text-[#94A3B8]" data-id="element-6254" data-mp="2jq6agof5t6pi">
                    Which pack size is pre-selected when the page loads
                  </p>
                </div>
              </div>
              <div data-id="element-6255" data-mp="3cdsnenaa1np1">
                <label className="block text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider mb-4" data-id="element-6256" data-mp="37fhgolfa3tb1">
                  Display Options
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-6257" data-mp="32ejodg0k1ivt">
                  {[{
                key: 'showPackImage',
                label: 'Show pack image',
                desc: 'Display the image uploaded in Pack Sizes'
              }, {
                key: 'showPackDescription',
                label: 'Show pack description',
                desc: 'Display the description set in Pack Sizes'
              }, {
                key: 'showPriceRange',
                label: 'Show price range',
                desc: 'Display "From $XX/shipment" per pack size'
              }, {
                key: 'showTierBadge',
                label: 'Show tier badge',
                desc: 'Display loyalty tier name (Bronze, Silver, Gold) on pack cards'
              }, {
                key: 'showBottleCount',
                label: 'Show bottle count',
                desc: 'Display the number of bottles in each pack'
              }].map(({
                key,
                label,
                desc
              }) => <div key={key} onClick={() => setPackDisplayOptions(prev => ({
                ...prev,
                [key]: !prev[key as keyof typeof prev]
              }))} className={`rounded-lg p-4 flex gap-3 border cursor-pointer transition-colors ${packDisplayOptions[key as keyof typeof packDisplayOptions] ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`} data-id="element-6258" data-mp="z7ud0wt0a8rq">
                      <div className="pt-0.5 flex-shrink-0" data-id="element-6259" data-mp="2ox7n7809kfgu">
                        <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${packDisplayOptions[key as keyof typeof packDisplayOptions] ? 'bg-[#0EA5E9] border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-6260" data-mp="2oz0tunavmzou">
                          {packDisplayOptions[key as keyof typeof packDisplayOptions] && <Check className="w-3 h-3 text-white" strokeWidth={3} data-id="element-6261" data-mp="3bvatrgb7j3px" />}
                        </div>
                      </div>
                      <div data-id="element-6262" data-mp="3lnqbqdmi7sat">
                        <div className="text-[14px] font-semibold text-[#0F172A]" data-id="element-6263" data-mp="3llvp8uvl3s4m">
                          {label}
                        </div>
                        <div className="text-[13px] text-[#64748B]" data-id="element-6264" data-mp="3vle6h3k40zsz">{desc}</div>
                      </div>
                    </div>)}
                </div>
              </div>
            </div>}

          {activeSettingsTab !== 'product-browser' && activeSettingsTab !== 'frequency-picker' && activeSettingsTab !== 'order-summary' && activeSettingsTab !== 'pack-selector' && <div className="py-12 text-center text-[#64748B] bg-[#F8FAFC] rounded-lg border border-dashed border-[#E2E8F0]" data-id="element-6265" data-mp="i3r4kl625a9f">
                <p data-id="element-6266" data-mp="46m5x68obdo7">Settings for {activeSettingsTab} coming soon...</p>
              </div>}
        </div>
      </Card>

      {/* Section 7: Live Preview */}
      <Card padding="none" className="border border-[#E2E8F0]" data-id="element-6267" data-mp="3qj1eugk3plsa">
        <div className="p-6" data-id="element-6268" data-mp="3vasw20hdaf5r">
          <h2 className="text-[16px] font-semibold text-[#0F172A] mb-1" data-id="element-6269" data-mp="3vb2pxz5j96gq">
            Live Preview
          </h2>
          <p className="text-[13px] text-[#64748B] mb-6" data-id="element-6270" data-mp="3hkpehmtfn9mn">
            How your widget will look with the current theme settings
          </p>

          <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-id="element-6271" data-mp="3mg4og3obklds">
            <div className="border-b border-[#E2E8F0] bg-white p-3 flex items-center justify-between" data-id="element-6272" data-mp="37m056iflqxi0">
              <span className="text-[11px] font-bold text-[#94A3B8] uppercase tracking-wider" data-id="element-6273" data-mp="3cmfij9zvw4zz">
                PREVIEW
              </span>
              <div className="flex items-center gap-1" data-id="element-6274" data-mp="3o07rbb6npj35">
                <button onClick={() => setPreviewMode('desktop')} className={`p-1.5 rounded ${previewMode === 'desktop' ? 'bg-[#0F172A] text-white' : 'text-[#64748B] hover:bg-[#F1F5F9]'}`} data-id="element-6275" data-mp="3nymvli8lvu3p">
                  <Monitor className="w-4 h-4" data-id="element-6276" data-mp="3e2bhcmpggu6m" />
                </button>
                <button onClick={() => setPreviewMode('mobile')} className={`p-1.5 rounded ${previewMode === 'mobile' ? 'bg-[#0F172A] text-white' : 'text-[#64748B] hover:bg-[#F1F5F9]'}`} data-id="element-6277" data-mp="394i9lp8i7s48">
                  <Smartphone className="w-4 h-4" data-id="element-6278" data-mp="30adh71dq28ml" />
                </button>
              </div>
            </div>

            <div className="bg-[#F8FAFC] p-8" data-id="element-6279" data-mp="2vdd7mghnynqq">
              <div className="flex flex-col md:flex-row gap-6" data-id="element-6280" data-mp="2qczahthloy7h">
                {/* Product Card 1 */}
                <div className="bg-white rounded-lg p-4 flex-1" style={{
                boxShadow: cardShadow === 'subtle' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                borderRadius: borderRadius,
                border: `${borderWeight} solid #E2E8F0`
              }} data-id="element-6281" data-mp="2ldw2q1a8kdvt">
                  <div className="relative aspect-square bg-[#FDF2F8] rounded-md mb-4 flex items-center justify-center" data-id="element-6282" data-mp="2gb4z2qvptgho">
                    <span className="text-4xl" data-id="element-6283" data-mp="2q6ycech9de0i">🍷</span>
                    <span className="absolute top-2 right-2 px-2 py-0.5 text-[11px] font-bold text-white rounded" style={{
                    backgroundColor: '#722F37'
                  }} data-id="element-6284" data-mp="2q6q0onwvgeqw">
                      10% off
                    </span>
                  </div>
                  <h3 className="text-[14px] text-[#0F172A] mb-1" style={{
                  fontWeight: headingWeight
                }} data-id="element-6285" data-mp="22gm6f75kprck">
                    2023 Shiraz Reserve
                  </h3>
                  <div className="flex items-center gap-2 mb-4" data-id="element-6286" data-mp="19rx4j5wp1hzr">
                    <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-6287" data-mp="1ert6kzltyzco">
                      $42.00
                    </span>
                    <span className="text-[12px] text-[#94A3B8] line-through" data-id="element-6288" data-mp="n2ovxrgle5ft">
                      $46.50
                    </span>
                  </div>
                  <button className="w-full py-2 text-[13px] font-medium transition-opacity hover:opacity-90" style={{
                  backgroundColor: '#722F37',
                  color: '#FFFFFF',
                  borderRadius: borderRadius
                }} data-id="element-6289" data-mp="d3hiz5drsct8">
                    Add to box
                  </button>
                </div>

                {/* Product Card 2 */}
                <div className="bg-white rounded-lg p-4 flex-1" style={{
                boxShadow: cardShadow === 'subtle' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                borderRadius: borderRadius,
                border: `${borderWeight} solid #E2E8F0`
              }} data-id="element-6290" data-mp="93pbkw044cpg">
                  <div className="relative aspect-square bg-[#FFFBEB] rounded-md mb-4 flex items-center justify-center" data-id="element-6291" data-mp="3hihbyrykmbhx">
                    <span className="text-4xl" data-id="element-6292" data-mp="32uz6hrd6pcug">🥂</span>
                    <span className="absolute top-2 right-2 px-2 py-0.5 text-[11px] font-bold text-white rounded" style={{
                    backgroundColor: '#722F37'
                  }} data-id="element-6293" data-mp="32vs86dxadc5q">
                      10% off
                    </span>
                  </div>
                  <h3 className="text-[14px] text-[#0F172A] mb-1" style={{
                  fontWeight: headingWeight
                }} data-id="element-6294" data-mp="36wazm6v9og7e">
                    2024 Pinot Grigio
                  </h3>
                  <div className="flex items-center gap-2 mb-4" data-id="element-6295" data-mp="3gvjr8t2ntiql">
                    <span className="text-[14px] font-semibold text-[#0F172A]" data-id="element-6296" data-mp="3gsxg6jgk8l3m">
                      $29.00
                    </span>
                    <span className="text-[12px] text-[#94A3B8] line-through" data-id="element-6297" data-mp="3qkbe4msv5cun">
                      $32.00
                    </span>
                  </div>
                  <button className="w-full py-2 text-[13px] font-medium transition-opacity hover:opacity-90" style={{
                  backgroundColor: '#722F37',
                  color: '#FFFFFF',
                  borderRadius: borderRadius
                }} data-id="element-6298" data-mp="8clweo15fv55">
                    Add to box
                  </button>
                </div>

                {/* Frequency Selector */}
                <div className="bg-white rounded-lg p-4 w-64 flex-shrink-0" style={{
                boxShadow: cardShadow === 'subtle' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                borderRadius: borderRadius,
                border: `${borderWeight} solid #E2E8F0`
              }} data-id="element-6299" data-mp="366f9se05zl5h">
                  <h3 className="text-[11px] font-bold text-[#94A3B8] uppercase tracking-wider mb-3" data-id="element-6300" data-mp="3kziqyk9ufwvo">
                    FREQUENCY
                  </h3>
                  <div className="space-y-2" data-id="element-6301" data-mp="3tycku2vsi95j">
                    <div className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-md cursor-pointer hover:border-[#CBD5E1]" data-id="element-6302" data-mp="3tw02hom4dwry">
                      <div className="flex items-center gap-3" data-id="element-6303" data-mp="1zja00ihorf8y">
                        <div className="w-4 h-4 rounded-full border border-[#CBD5E1]" data-id="element-6304" data-mp="1s0k67zbcxsur" />
                        <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-6305" data-mp="1s2dfznh9ot6z">
                          Monthly
                        </span>
                      </div>
                      <span className="text-[11px] font-medium text-[#D4A574]" data-id="element-6306" data-mp="1ibackf0v824j">
                        10%
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 border-2 border-[#722F37] rounded-md cursor-pointer bg-[#FFFDF8]" data-id="element-6307" data-mp="2azy24msnddnw">
                      <div className="flex items-center gap-3" data-id="element-6308" data-mp="2614psxp3jspb">
                        <div className="w-4 h-4 rounded-full border-[5px] border-[#722F37]" data-id="element-6309" data-mp="210gyeawyh9uk" />
                        <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-6310" data-mp="1x0eya66vcohe">
                          Quarterly
                        </span>
                      </div>
                      <span className="text-[11px] font-medium text-[#722F37]" data-id="element-6311" data-mp="lkqajclsjlfj">
                        15%
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 border border-[#E2E8F0] rounded-md cursor-pointer hover:border-[#CBD5E1]" data-id="element-6312" data-mp="29fv53vwzra6g">
                      <div className="flex items-center gap-3" data-id="element-6313" data-mp="1zjicl1d6n2da">
                        <div className="w-4 h-4 rounded-full border border-[#CBD5E1]" data-id="element-6314" data-mp="1zh5mg0x9tlp5" />
                        <span className="text-[13px] font-medium text-[#0F172A]" data-id="element-6315" data-mp="24icy4b2jb513">
                          Biannual
                        </span>
                      </div>
                      <span className="text-[11px] font-medium text-[#D4A574]" data-id="element-6316" data-mp="2x5fscr06gtlj">
                        20%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Navigation Footer */}
      <div className="flex items-center justify-between pt-2 pb-4" data-id="element-6317" data-mp="17qj0cq42xs8m">
        <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-[14px] font-medium text-[#475569] hover:text-[#0F172A] border border-[#E2E8F0] bg-white rounded-[6px] hover:border-[#CBD5E1] transition-colors" data-id="element-6318" data-mp="1buh09318b72q">
          <ArrowLeft className="w-4 h-4" data-id="element-6319" data-mp="zak1esk56zlk" />
          Flow
        </button>
        <button onClick={onNext} className="flex items-center gap-2 px-5 py-2 text-[14px] font-medium text-white bg-[#0EA5E9] hover:bg-[#0284C7] rounded-[6px] transition-colors" data-id="element-6320" data-mp="1e71d5kw8ug8n">
          Review Selling Plans
          <ArrowRight className="w-4 h-4" data-id="element-6321" data-mp="lk6r4jjs38t2" />
        </button>
      </div>
    </div>;
}